import { manufacturingFetch } from "@/lib/manufacturingClient";
import type { LaborPaysBy } from "@/lib/manufacturing/laborPayQuantity";
import type {
  MfgLaborBayDeleteResponse,
  MfgLaborBayKind,
  MfgLaborBayMutationResponse,
  MfgLaborBaysListResponse,
  MfgLaborRateBayLinkDeleteResponse,
  MfgLaborRateBayLinksListResponse,
  MfgLaborRateBayLinksSetResponse,
  MfgLaborRateItemDeleteResponse,
  MfgLaborRateItemMutationResponse,
  MfgLaborRateItemsListResponse,
} from "@/lib/manufacturing/laborBillingTypes";

export function listManufacturingLaborBays() {
  return manufacturingFetch<MfgLaborBaysListResponse>("/api/mfg/labor-bays", { method: "GET" });
}

export function createManufacturingLaborBay(body: {
  bay_code: string;
  name: string;
  kind?: MfgLaborBayKind;
  sort_order?: number;
  is_active?: boolean;
  notes?: string;
}) {
  return manufacturingFetch<MfgLaborBayMutationResponse>("/api/mfg/labor-bays/create", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function updateManufacturingLaborBay(body: {
  bay_id: string;
  bay_code?: string;
  name?: string;
  kind?: MfgLaborBayKind;
  sort_order?: number;
  is_active?: boolean;
  notes?: string;
}) {
  return manufacturingFetch<MfgLaborBayMutationResponse>("/api/mfg/labor-bays/update", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function deleteManufacturingLaborBay(body: { bay_id: string }) {
  return manufacturingFetch<MfgLaborBayDeleteResponse>("/api/mfg/labor-bays/delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function listManufacturingLaborRateItems() {
  return manufacturingFetch<MfgLaborRateItemsListResponse>("/api/mfg/labor-rate-items", {
    method: "GET",
  });
}

export function createManufacturingLaborRateItem(body: {
  item_code: string;
  label: string;
  flat_rate_usd?: number;
  build_model_id?: string | null;
  recipe_id?: string | null;
  option_code?: string;
  roof_eligible?: boolean;
  is_active?: boolean;
  sort_order?: number;
  notes?: string;
  pays_by?: LaborPaysBy | null;
}) {
  return manufacturingFetch<MfgLaborRateItemMutationResponse>("/api/mfg/labor-rate-items/create", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function updateManufacturingLaborRateItem(body: {
  rate_item_id: string;
  item_code?: string;
  label?: string;
  flat_rate_usd?: number;
  build_model_id?: string | null;
  recipe_id?: string | null;
  option_code?: string;
  roof_eligible?: boolean;
  is_active?: boolean;
  sort_order?: number;
  notes?: string;
  pays_by?: LaborPaysBy | null;
}) {
  return manufacturingFetch<MfgLaborRateItemMutationResponse>("/api/mfg/labor-rate-items/update", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function deleteManufacturingLaborRateItem(body: { rate_item_id: string }) {
  return manufacturingFetch<MfgLaborRateItemDeleteResponse>("/api/mfg/labor-rate-items/delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function listManufacturingLaborRateBayLinks(filters?: {
  rate_item_id?: string;
  bay_id?: string;
}) {
  const qs = new URLSearchParams();
  if (filters?.rate_item_id) qs.set("rate_item_id", filters.rate_item_id);
  if (filters?.bay_id) qs.set("bay_id", filters.bay_id);
  const suffix = qs.toString();
  return manufacturingFetch<MfgLaborRateBayLinksListResponse>(
    `/api/mfg/labor-rate-bay-links${suffix ? `?${suffix}` : ""}`,
    { method: "GET" }
  );
}

export function setManufacturingLaborRateBayLinks(body: {
  rate_item_id: string;
  bay_ids: string[];
}) {
  return manufacturingFetch<MfgLaborRateBayLinksSetResponse>("/api/mfg/labor-rate-bay-links/set", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function deleteManufacturingLaborRateBayLink(body: { link_id: string }) {
  return manufacturingFetch<MfgLaborRateBayLinkDeleteResponse>(
    "/api/mfg/labor-rate-bay-links/delete",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
}

export type {
  MfgLaborBay,
  MfgLaborBayKind,
  MfgLaborRateBayLink,
  MfgLaborRateItem,
} from "@/lib/manufacturing/laborBillingTypes";
